import React, { useEffect, useState } from 'react';
import { getSessionGuesses, SessionGuesses } from '../lib/guesses';

interface Props {
  testId: string;
  /** Opens the unlock options (friends / Premium). Changing `reloadKey` re-checks after an unlock. */
  onUnlock: () => void;
  reloadKey?: unknown;
}

// "Who they think you are" — on the sender's results page. The count is free;
// the names come back from the database only once this session is unlocked.
export default function GuessesCard({ testId, onUnlock, reloadKey }: Props) {
  const [data, setData] = useState<SessionGuesses | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSessionGuesses(testId).then(d => { if (!cancelled) setData(d); });
    return () => { cancelled = true; };
  }, [testId, reloadKey]);

  if (!data || data.count === 0) return null;

  return (
    <div className="rounded-3xl overflow-hidden border border-brand-border mb-8" style={{ background: 'var(--color-brand-card)' }}>
      <div style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)', padding: '18px 20px', textAlign: 'center' }}>
        <p style={{ color: '#fff', fontWeight: 900, fontSize: 18, letterSpacing: -0.3 }}>
          👀 {data.count} {data.count === 1 ? 'guess' : 'guesses'} about who you are
        </p>
      </div>

      <div style={{ padding: '18px 20px 20px' }}>
        {data.unlocked ? (
          <>
            <p className="text-sm text-zinc-500 mb-3">Here's who they think sent it:</p>
            <div className="flex flex-wrap gap-2">
              {data.guesses.map(g => (
                <span
                  key={g.name}
                  className="px-4 py-2 rounded-full text-sm font-black border border-brand-border"
                  style={{ color: 'var(--color-brand-text)', background: 'var(--color-brand-bg)' }}
                >
                  {g.name}
                  {g.times > 1 && <span className="text-brand-accent ml-1.5">×{g.times}</span>}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-zinc-600 mt-3">These are guesses, not proof. Nobody is ever told if they're right.</p>
          </>
        ) : (
          <>
            {/* placeholder bars only — the real names never reach this page until unlocked */}
            <div aria-hidden="true" className="flex flex-wrap gap-2 mb-4" style={{ filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' }}>
              {[96, 64, 80].slice(0, Math.max(1, Math.min(3, data.count))).map(w => (
                <span key={w} className="rounded-full" style={{ display: 'inline-block', width: w, height: 34, background: '#e4c9d3' }} />
              ))}
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              {data.count === 1 ? 'Someone took a guess' : 'People took guesses'} at who sent this. Unlock to see the names.
            </p>
            <button
              onClick={onUnlock}
              data-track="guess_unlock"
              className="w-full font-black text-base py-4 rounded-full flex items-center justify-center gap-2 transition-transform active:scale-[0.97] cursor-pointer"
              style={{ background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff', boxShadow: '0 10px 24px rgba(225,29,72,0.28)' }}
            >
              See who they think you are
            </button>
          </>
        )}
      </div>
    </div>
  );
}
